/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { Link } from '@/components/link';
import {
    Breadcrumb,
    BreadcrumbList,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { SeoMeta } from '@/components/seo-meta';
import { routeHref, routes } from '@/route-paths';

// ─── Table components ─────────────────────────────────────────────────────────

function Table({ headers, rows }: { headers: string[]; rows: (string | number)[][] }) {
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
                <thead>
                    <tr className="bg-foreground text-background">
                        {headers.map((h) => (
                            <th key={h} className="px-4 py-3 text-left font-medium whitespace-nowrap">
                                {h}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => (
                        // oxlint-disable-next-line react/no-array-index-key -- static size-guide table rows, order is fixed
                        <tr key={i} className={i % 2 === 0 ? 'bg-background' : 'bg-[#F5F3F0]'}>
                            {row.map((cell, j) => (
                                // oxlint-disable-next-line react/no-array-index-key -- static size-guide table cells, order is fixed
                                <td key={j} className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                                    {cell}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function Section({ title, children, note }: { title: string; children: ReactElement; note?: string }) {
    return (
        <div className="mb-12">
            <h2 style={{ fontFamily: 'var(--font-display)' }} className="text-3xl font-light mb-2 text-foreground">
                {title}
            </h2>
            {note && <p className="text-sm text-muted-foreground mb-5">{note}</p>}
            {children}
        </div>
    );
}

// ─── Bag size data ─────────────────────────────────────────────────────────────
// Dimensions based on Baggallini product catalog data

const MINI_HEADERS = ['Style', 'Width (in.)', 'Height (in.)', 'Depth (in.)', 'Strap Drop (in.)'];
const MINI_ROWS: (string | number)[][] = [
    ['Mini Crossbody', 6, 5, 1.5, '22–24'],
    ['Slim Mini Bag', 5, 6, 1, '23–25'],
    ['Micro Wallet Crossbody', 4.5, 4, 1, '21–23'],
];

const CROSSBODY_HEADERS = ['Style', 'Width (in.)', 'Height (in.)', 'Depth (in.)', 'Strap Drop (in.)'];
const CROSSBODY_ROWS: (string | number)[][] = [
    ['Slim Crossbody', 8, 6, 1.5, '24–44'],
    ['Classic Crossbody', 9, 7, 2.5, '23–44'],
    ['Large Crossbody', 11, 8, 3, '24–44'],
    ['Hobo Crossbody', 13, 10, 3, '22–42'],
];

const SLING_HEADERS = ['Style', 'Width (in.)', 'Height (in.)', 'Depth (in.)', 'Strap Length (in.)'];
const SLING_ROWS: (string | number)[][] = [
    ['Mini Sling', 7, 5, 1.5, '28–50'],
    ['Classic Sling', 8, 6.5, 2, '26–50'],
    ['Waistpack / Belt Bag', 9, 5, 2.5, '26–44'],
];

const BACKPACK_HEADERS = ['Style', 'Width (in.)', 'Height (in.)', 'Depth (in.)'];
const BACKPACK_ROWS: (string | number)[][] = [
    ['Mini Backpack', 9, 11, 3.5],
    ['Day Pack', 11, 14, 5],
    ['Large Backpack', 12, 16, 6],
];

const TOTE_HEADERS = ['Style', 'Width (in.)', 'Height (in.)', 'Depth (in.)', 'Handle Drop (in.)'];
const TOTE_ROWS: (string | number)[][] = [
    ['Small Tote', 11, 10, 3.5, 8],
    ['Medium Tote', 14, 12, 4, 9],
    ['Large Tote', 16, 13, 5, 10],
    ['Market Tote', 18, 14, 5, 11],
];

const TRAVEL_HEADERS = ['Style', 'Width (in.)', 'Height (in.)', 'Depth (in.)', 'Notes'];
const TRAVEL_ROWS: (string | number)[][] = [
    ['Tech Organizer Pouch', 9, 6, 1.5, 'Lay-flat laptop sleeve'],
    ['Packing Cube Set (S/M)', '11 / 13', '7 / 9', '2 / 3', 'Compression zipper'],
    ['Personal Item Tote', 14, 11, 6, 'Fits under airline seat'],
    ['Rolling Carry-On', 14, 22, 9, 'FAA cabin-approved'],
];

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function SizeGuide(): ReactElement {
    return (
        <div className="pb-16">
            <SeoMeta
                title="Bag Size Guide — Baggallini"
                description="Find the right Baggallini bag size with our complete size guide. Dimensions for crossbody bags, backpacks, totes, slings, and travel bags."
            />

            {/* Breadcrumb */}
            <div className="max-w-screen-2xl mx-auto px-4 pt-4 pb-2">
                <Breadcrumb>
                    <BreadcrumbList>
                        <BreadcrumbItem>
                            <BreadcrumbLink asChild>
                                <Link to="/">Home</Link>
                            </BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                            <BreadcrumbPage>Bag Size Guide</BreadcrumbPage>
                        </BreadcrumbItem>
                    </BreadcrumbList>
                </Breadcrumb>
            </div>

            <div className="max-w-screen-lg mx-auto px-4 pt-10">
                {/* Header */}
                <div className="mb-12">
                    <h1
                        style={{ fontFamily: 'var(--font-display)' }}
                        className="text-5xl font-light tracking-wide mb-4">
                        Bag Size Guide
                    </h1>
                    <p className="text-muted-foreground leading-relaxed max-w-2xl">
                        Not sure which size is right for you? Use this guide to compare dimensions across our bag
                        families. All measurements are approximate and may vary slightly by colorway or season. If your
                        essentials fall between two sizes, we recommend going with the larger option.
                    </p>
                </div>

                {/* How to choose callout */}
                <div className="bg-[#F5F3F0] rounded-2xl px-8 py-6 mb-12 grid sm:grid-cols-3 gap-6 text-center">
                    {[
                        { icon: '👜', label: 'Mini & Crossbody', desc: 'Phone, cards, keys, lip balm' },
                        { icon: '🎒', label: 'Backpack & Tote', desc: 'Tablet, daily essentials, snacks' },
                        { icon: '✈️', label: 'Travel', desc: 'Laptop, documents, everything in between' },
                    ].map((item) => (
                        <div key={item.label}>
                            <div className="text-2xl mb-2">{item.icon}</div>
                            <p className="text-sm font-medium text-foreground mb-1">{item.label}</p>
                            <p className="text-xs text-muted-foreground">{item.desc}</p>
                        </div>
                    ))}
                </div>

                <Section title="Mini Bags" note="Perfect for evenings out or when you want to travel ultra-light.">
                    <Table headers={MINI_HEADERS} rows={MINI_ROWS} />
                </Section>

                <Section
                    title="Crossbody Bags"
                    note="Our most popular silhouette — hands-free, organized, and perfect for everyday wear.">
                    <Table headers={CROSSBODY_HEADERS} rows={CROSSBODY_ROWS} />
                </Section>

                <Section title="Slings & Waistpacks" note="Adjustable straps that work cross-body or around the waist.">
                    <Table headers={SLING_HEADERS} rows={SLING_ROWS} />
                </Section>

                <Section title="Backpacks" note="Built with organized pockets and padded straps for comfort on the go.">
                    <Table headers={BACKPACK_HEADERS} rows={BACKPACK_ROWS} />
                </Section>

                <Section title="Tote Bags" note="From market runs to office days — a tote for every occasion.">
                    <Table headers={TOTE_HEADERS} rows={TOTE_ROWS} />
                </Section>

                <Section
                    title="Travel Bags"
                    note="Organized travel starts before you leave home. Many styles feature RFID-blocking and TSA-friendly layouts.">
                    <Table headers={TRAVEL_HEADERS} rows={TRAVEL_ROWS} />
                </Section>

                {/* Notes */}
                <div className="bg-[#F5F3F0] rounded-2xl px-8 py-6 space-y-3 text-sm text-muted-foreground mb-8">
                    <p>
                        <strong className="text-foreground">Not sure?</strong> Our customer service team can help you
                        find the right bag for your needs. Call us at{' '}
                        <a href="tel:18004488753" className="text-foreground hover:underline">
                            1-800-448-8753
                        </a>{' '}
                        Mon–Fri, 9am–5pm ET.
                    </p>
                    <p>
                        <strong className="text-foreground">Returns:</strong> Items may be returned within 30 days if
                        unused and in original condition.{' '}
                        <Link to="/returns" className="text-foreground hover:underline">
                            See our full return policy.
                        </Link>
                    </p>
                    <p>
                        <strong className="text-foreground">Anti-Theft Styles:</strong> Look for the Securtex® label for
                        slash-resistant straps, locking zippers, and RFID-blocking pockets.{' '}
                        <Link
                            to={routeHref(routes.category, { categoryId: 'anti-theft' })}
                            className="text-foreground hover:underline">
                            Shop Anti-Theft.
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
